import { useCallback, useMemo, useState } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Grid,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
  Progress,
  Portal,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Switch,
} from '@chakra-ui/react'
import {
  X,
  Trash2,
  ChevronUp,
  ChevronDown,
  CalendarClock,
  Calendar,
  Send,
  CheckCircle,
  AlertTriangle,
  Users,
  Wallet,
  CircleDollarSign,
  Clock,
  ChevronRight,
  MoreVertical,
  FileCheck,
} from 'lucide-react'
import { useFundCall, useDeleteFundCall, useIssueFundCall, useSendFundCall, useFundCallItemsWithDetails } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { FundCallStatus, FundCallItem, FundCallItemDetail } from '../types'

// Composant pour la vue détaillée d'un item
interface DetailedItemViewProps {
  item: FundCallItem & { fund_call_item_details: FundCallItemDetail[] }
  formatCurrency: (value: number | null | undefined) => string
  getItemStatusBadge: (status: string) => JSX.Element
}

function DetailedItemView({ item, formatCurrency, getItemStatusBadge }: DetailedItemViewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const details = item.fund_call_item_details || []
  const remaining = (item.amount ?? 0) - (item.paid_amount ?? 0)

  // Grouper les détails par budget
  const detailsByBudget = useMemo(() => {
    const grouped: Record<string, FundCallItemDetail[]> = {}
    details.forEach((detail) => {
      const budgetName = detail.budget_name || 'Sans budget'
      if (!grouped[budgetName]) {
        grouped[budgetName] = []
      }
      grouped[budgetName].push(detail)
    })
    return grouped
  }, [details])

  return (
    <Box>
      {/* Header clickable */}
      <HStack
        px={4}
        py={3}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        _hover={{ bg: 'gray.50' }}
        transition="background 0.15s"
      >
        <Box color="gray.400">
          <ChevronRight
            size={16}
            style={{
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
            }}
          />
        </Box>
        <Box flex={1}>
          <HStack gap={3}>
            <Text fontWeight="semibold" minW="60px">
              {item.unit?.unit_number || '-'}
            </Text>
            <Text fontSize="sm" color="gray.700">
              {item.owner?.first_name} {item.owner?.last_name}
            </Text>
          </HStack>
        </Box>
        <HStack gap={4}>
          <VStack align="flex-end" gap={0}>
            <Text fontSize="sm" fontWeight="semibold">
              {formatCurrency(item.amount)}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {details.length} ligne{details.length > 1 ? 's' : ''} de calcul
            </Text>
          </VStack>
          <VStack align="flex-end" gap={0}>
            <Text fontSize="sm" color={remaining > 0 ? 'orange.600' : 'green.600'}>
              {formatCurrency(remaining)}
            </Text>
            <Text fontSize="xs" color="gray.500">reste</Text>
          </VStack>
          {getItemStatusBadge(item.status)}
        </HStack>
      </HStack>

      {/* Détails expansibles */}
      {isExpanded && details.length > 0 && (
        <Box bg="gray.50" px={4} py={3} borderTopWidth="1px" borderColor="gray.100">
          {Object.entries(detailsByBudget).map(([budgetName, budgetDetails]) => (
            <Box key={budgetName} mb={3} _last={{ mb: 0 }}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} textTransform="uppercase">
                {budgetName}
              </Text>
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Ligne budgétaire</Table.ColumnHeader>
                    <Table.ColumnHeader>Clé de répartition</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Quote-part</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Total</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">%</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Base</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">Montant</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {budgetDetails.map((detail) => (
                    <Table.Row key={detail.id}>
                      <Table.Cell>
                        <Text fontSize="xs">{detail.budget_line_label}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge size="sm" variant="subtle">
                          {detail.distribution_key_name}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontSize="xs">{Number(detail.share_value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontSize="xs" color="gray.500">{Number(detail.total_shares).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontSize="xs" fontWeight="medium">{Number(detail.percentage).toFixed(2)}%</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontSize="xs" color="gray.500">{formatCurrency(detail.base_amount)}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontSize="xs" fontWeight="semibold" color="brand.600">
                          {formatCurrency(detail.calculated_amount)}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          ))}
          {/* Total */}
          <Box mt={2} pt={2} borderTopWidth="1px" borderColor="gray.200">
            <HStack justify="flex-end" gap={4}>
              <Text fontSize="sm" color="gray.600">Total calculé :</Text>
              <Text fontSize="sm" fontWeight="bold" color="brand.700">
                {formatCurrency(details.reduce((sum, d) => sum + (d.calculated_amount ?? 0), 0))}
              </Text>
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface FundCallDetailDrawerProps {
  fundCallId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function FundCallDetailDrawer({
  fundCallId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: FundCallDetailDrawerProps) {
  const { data: fundCall, isLoading, error } = useFundCall(fundCallId ?? undefined)
  const { data: itemsWithDetails, isLoading: isLoadingDetails } = useFundCallItemsWithDetails(fundCallId ?? undefined)
  const deleteFundCall = useDeleteFundCall()
  const issueFundCall = useIssueFundCall()
  const sendFundCall = useSendFundCall()

  // View mode: 'synthetic' or 'detailed'
  const [viewMode, setViewMode] = useState<'synthetic' | 'detailed'>('synthetic')

  // Dialog states
  const [issueDialogOpen, setIssueDialogOpen] = useState(false)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleIssue = async () => {
    if (!fundCallId) return
    try {
      await issueFundCall.mutateAsync(fundCallId)
      toaster.create({
        title: 'Appel de fonds émis',
        description: 'L\'appel de fonds a été comptabilisé avec succès.',
        type: 'success',
      })
    } catch (error) {
      console.error('Error issuing fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'émission.',
        type: 'error',
      })
    } finally {
      setIssueDialogOpen(false)
    }
  }

  const handleSend = async () => {
    if (!fundCallId) return
    try {
      await sendFundCall.mutateAsync(fundCallId)
      toaster.create({
        title: 'Appel de fonds envoyé',
        description: 'L\'appel de fonds a été envoyé aux copropriétaires.',
        type: 'success',
      })
    } catch (error) {
      console.error('Error sending fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'envoi.',
        type: 'error',
      })
    } finally {
      setSendDialogOpen(false)
    }
  }

  const handleDelete = async () => {
    if (!fundCallId) return
    try {
      await deleteFundCall.mutateAsync(fundCallId)
      toaster.create({
        title: 'Appel de fonds supprimé',
        description: 'L\'appel de fonds a été supprimé avec succès.',
        type: 'success',
      })
      onClose()
    } catch (error) {
      console.error('Error deleting fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression.',
        type: 'error',
      })
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  const handleInteractOutside = useCallback((e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    const target = e.detail.originalEvent.target as HTMLElement
    const tableRow = target.closest('[data-fund-call-row]') as HTMLElement

    if (tableRow) {
      return
    }

    onClose()
  }, [onClose])

  const formatCurrency = (value: number | null | undefined) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const getStatusBadge = (status: FundCallStatus) => {
    const config: Record<FundCallStatus, { color: string; label: string; icon: typeof CheckCircle }> = {
      draft: { color: 'gray', label: 'Brouillon', icon: Clock },
      issued: { color: 'purple', label: 'Émis', icon: FileCheck },
      sent: { color: 'blue', label: 'Envoyé', icon: Send },
      partial: { color: 'orange', label: 'Partiellement payé', icon: AlertTriangle },
      paid: { color: 'green', label: 'Payé', icon: CheckCircle },
      overdue: { color: 'red', label: 'En retard', icon: AlertTriangle },
    }
    const { color, label, icon: Icon } = config[status] || { color: 'gray', label: status, icon: Clock }
    return (
      <Badge colorPalette={color} variant="subtle" display="flex" alignItems="center" gap={1}>
        <Icon size={12} />
        {label}
      </Badge>
    )
  }

  const getItemStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      draft: { color: 'gray', label: 'Brouillon' },
      issued: { color: 'purple', label: 'Émis' },
      sent: { color: 'blue', label: 'Envoyé' },
      partial: { color: 'orange', label: 'Partiel' },
      paid: { color: 'green', label: 'Payé' },
      overdue: { color: 'red', label: 'Retard' },
    }
    const { color, label } = config[status] || { color: 'gray', label: status }
    return (
      <Badge colorPalette={color} variant="subtle" size="sm">
        {label}
      </Badge>
    )
  }

  // Calculate statistics
  const stats = useMemo(() => {
    if (!fundCall?.fund_call_items) {
      return {
        totalAmount: fundCall?.total_amount ?? 0,
        totalPaid: 0,
        totalPending: 0,
        itemsCount: 0,
        paidCount: 0,
        partialCount: 0,
        pendingCount: 0,
        paymentRate: 0,
      }
    }

    const items = fundCall.fund_call_items
    const totalAmount = fundCall.total_amount ?? 0
    const totalPaid = items.reduce((sum, item) => sum + (item.paid_amount ?? 0), 0)
    const paidCount = items.filter(item => item.status === 'paid').length
    const partialCount = items.filter(item => item.status === 'partial').length
    const pendingCount = items.filter(item => ['sent', 'draft', 'overdue'].includes(item.status)).length

    return {
      totalAmount,
      totalPaid,
      totalPending: totalAmount - totalPaid,
      itemsCount: items.length,
      paidCount,
      partialCount,
      pendingCount,
      paymentRate: totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0,
    }
  }, [fundCall])

  return (
  <>
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="1000px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4}>
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation arrows */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {fundCall && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm">
                        N° {fundCall.call_number}
                      </Badge>
                      {getStatusBadge(fundCall.status)}
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {fundCall.label}
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      {fundCall.fund_call_budgets && fundCall.fund_call_budgets.length > 0
                        ? fundCall.fund_call_budgets.map((fcb) => fcb.budget?.name).join(', ')
                        : 'Sans budget'}{' '}
                      • {fundCall.fiscal_year?.name}
                    </Text>
                  </>
                )}
                {!fundCall && <Heading size="md">Détails de l'appel de fonds</Heading>}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} flexShrink={0}>
                {fundCall && (fundCall.status === 'draft' || fundCall.status === 'issued') && (
                  <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label="Actions"
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          {/* Émettre - disponible pour les brouillons */}
                          {fundCall.status === 'draft' && (
                            <MenuItem
                              value="issue"
                              onClick={() => setIssueDialogOpen(true)}
                            >
                              <HStack gap={2}>
                                <FileCheck size={16} />
                                <Text>Émettre</Text>
                              </HStack>
                            </MenuItem>
                          )}

                          {/* Envoyer - disponible pour les appels émis */}
                          {fundCall.status === 'issued' && (
                            <MenuItem
                              value="send"
                              onClick={() => setSendDialogOpen(true)}
                            >
                              <HStack gap={2}>
                                <Send size={16} />
                                <Text>Envoyer</Text>
                              </HStack>
                            </MenuItem>
                          )}

                          {/* Supprimer - uniquement pour les brouillons */}
                          {fundCall.status === 'draft' && (
                            <MenuItem
                              value="delete"
                              onClick={() => setDeleteDialogOpen(true)}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>Supprimer</Text>
                              </HStack>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                )}
                <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                  <X size={18} />
                </IconButton>
              </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Appel de fonds non trouvé" />}

            {fundCall && (
              <VStack align="stretch" gap={0} flex={1} height="100%">
                {/* Timestamps & Dates */}
                <HStack
                  gap={3}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="150px">
                    <Box color="blue.500">
                      <CalendarClock size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Date de l'appel
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(fundCall.call_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="150px">
                    <Box color="orange.500">
                      <Calendar size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Date d'échéance
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(fundCall.due_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  {fundCall.issued_at && (
                    <HStack gap={2} flex={1} minW="150px">
                      <Box color="purple.500">
                        <FileCheck size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          Comptabilisé le
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {formatDate(fundCall.issued_at)}
                        </Text>
                      </VStack>
                    </HStack>
                  )}

                  {fundCall.sent_at && (
                    <HStack gap={2} flex={1} minW="150px">
                      <Box color="green.500">
                        <Send size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          Envoyé le
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {formatDate(fundCall.sent_at)}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </HStack>

                {/* Stats Cards */}
                <Box px={6} pt={4}>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Wallet size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Montant total
                            </Text>
                          </HStack>
                          <Text fontSize="xl" fontWeight="bold" color="brand.600">
                            {formatCurrency(stats.totalAmount)}
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <CircleDollarSign size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Encaissé
                            </Text>
                          </HStack>
                          <Text fontSize="xl" fontWeight="bold" color="green.600">
                            {formatCurrency(stats.totalPaid)}
                          </Text>
                          <Progress.Root value={stats.paymentRate} size="sm" colorPalette="green">
                            <Progress.Track>
                              <Progress.Range />
                            </Progress.Track>
                          </Progress.Root>
                          <Text fontSize="xs" color="gray.500">
                            {stats.paymentRate.toFixed(1)}% du total
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <AlertTriangle size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Reste à encaisser
                            </Text>
                          </HStack>
                          <Text fontSize="xl" fontWeight="bold" color={stats.totalPending > 0 ? 'orange.600' : 'gray.600'}>
                            {formatCurrency(stats.totalPending)}
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Users size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Copropriétaires
                            </Text>
                          </HStack>
                          <Text fontSize="xl" fontWeight="bold">
                            {stats.itemsCount}
                          </Text>
                          <HStack gap={2} flexWrap="wrap">
                            {stats.paidCount > 0 && (
                              <Badge colorPalette="green" size="sm">{stats.paidCount} payé{stats.paidCount > 1 ? 's' : ''}</Badge>
                            )}
                            {stats.partialCount > 0 && (
                              <Badge colorPalette="orange" size="sm">{stats.partialCount} partiel{stats.partialCount > 1 ? 's' : ''}</Badge>
                            )}
                            {stats.pendingCount > 0 && (
                              <Badge colorPalette="gray" size="sm">{stats.pendingCount} en attente</Badge>
                            )}
                          </HStack>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Grid>
                </Box>

                {/* Description */}
                {fundCall.description && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Body>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Description
                        </Text>
                        <Text>{fundCall.description}</Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Budgets associés */}
                {fundCall.fund_call_budgets && fundCall.fund_call_budgets.length > 0 && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                        <Heading size="sm">Budgets associés</Heading>
                      </Card.Header>
                      <Card.Body p={0}>
                        <Table.Root size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Budget</Table.ColumnHeader>
                              <Table.ColumnHeader>Type</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Budget total</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Montant appelé</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {fundCall.fund_call_budgets.map((fcb) => (
                              <Table.Row key={fcb.id}>
                                <Table.Cell fontWeight="medium">{fcb.budget?.name || '-'}</Table.Cell>
                                <Table.Cell>
                                  <Badge size="sm" variant="subtle">
                                    {fcb.budget?.budget_type === 'operating'
                                      ? 'Charges courantes'
                                      : fcb.budget?.budget_type === 'works'
                                        ? 'Travaux'
                                        : fcb.budget?.budget_type === 'reserve'
                                          ? 'Fonds de réserve'
                                          : fcb.budget?.budget_type}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell textAlign="right" color="gray.600">
                                  {formatCurrency(fcb.budget?.total_amount)}
                                </Table.Cell>
                                <Table.Cell textAlign="right" fontWeight="semibold">
                                  {formatCurrency(fcb.amount)}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Fund Call Items */}
                <Box px={6} pt={4} pb={6} flex={1} overflow="auto">
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between" align="center">
                        <Heading size="sm">Détail par copropriétaire</Heading>
                        <HStack
                          gap={2}
                          cursor="pointer"
                          onClick={() => setViewMode(viewMode === 'detailed' ? 'synthetic' : 'detailed')}
                        >
                          <Text fontSize="sm" color={viewMode === 'synthetic' ? 'brand.600' : 'gray.500'} fontWeight={viewMode === 'synthetic' ? 'medium' : 'normal'}>
                            Synthétique
                          </Text>
                          <Switch.Root
                            checked={viewMode === 'detailed'}
                            colorPalette="brand"
                            size="sm"
                          >
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                          </Switch.Root>
                          <Text fontSize="sm" color={viewMode === 'detailed' ? 'brand.600' : 'gray.500'} fontWeight={viewMode === 'detailed' ? 'medium' : 'normal'}>
                            Détaillé
                          </Text>
                        </HStack>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      {viewMode === 'synthetic' ? (
                        // Vue synthétique (actuelle)
                        fundCall.fund_call_items && fundCall.fund_call_items.length > 0 ? (
                          <Table.Root size="sm">
                            <Table.Header>
                              <Table.Row>
                                <Table.ColumnHeader>Lot</Table.ColumnHeader>
                                <Table.ColumnHeader>Copropriétaire</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="right">Montant dû</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="right">Payé</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="right">Reste</Table.ColumnHeader>
                                <Table.ColumnHeader>Statut</Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {fundCall.fund_call_items.map((item) => {
                                const remaining = (item.amount ?? 0) - (item.paid_amount ?? 0)
                                return (
                                  <Table.Row key={item.id}>
                                    <Table.Cell>
                                      <Text fontWeight="medium">{item.unit?.unit_number || '-'}</Text>
                                      {item.unit?.stairwell && (
                                        <Text fontSize="xs" color="gray.500">{item.unit.stairwell}</Text>
                                      )}
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Text fontSize="sm">
                                        {item.owner?.first_name} {item.owner?.last_name}
                                      </Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                      <Text fontSize="sm" fontWeight="medium">
                                        {formatCurrency(item.amount)}
                                      </Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                      <Text fontSize="sm" color={item.paid_amount ? 'green.600' : 'gray.400'}>
                                        {formatCurrency(item.paid_amount)}
                                      </Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                      <Text
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color={remaining > 0 ? 'orange.600' : 'green.600'}
                                      >
                                        {formatCurrency(remaining)}
                                      </Text>
                                    </Table.Cell>
                                    <Table.Cell>
                                      {getItemStatusBadge(item.status)}
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              })}
                            </Table.Body>
                          </Table.Root>
                        ) : (
                          <Box textAlign="center" py={8} color="gray.500">
                            <Text>Aucune ligne d'appel de fonds</Text>
                          </Box>
                        )
                      ) : (
                        // Vue détaillée
                        isLoadingDetails ? (
                          <Box textAlign="center" py={8}>
                            <Text color="gray.500">Chargement des détails...</Text>
                          </Box>
                        ) : itemsWithDetails && itemsWithDetails.length > 0 ? (
                          <VStack align="stretch" gap={0} divideY="1px" divideColor="gray.200">
                            {itemsWithDetails.map((item) => (
                              <DetailedItemView
                                key={item.id}
                                item={item}
                                formatCurrency={formatCurrency}
                                getItemStatusBadge={getItemStatusBadge}
                              />
                            ))}
                          </VStack>
                        ) : (
                          <Box textAlign="center" py={8} color="gray.500">
                            <Text>Aucun détail de calcul disponible</Text>
                          </Box>
                        )
                      )}
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>

    {/* Dialog Émettre */}
    <ConfirmDialog
      isOpen={issueDialogOpen}
      onClose={() => setIssueDialogOpen(false)}
      onConfirm={handleIssue}
      title="Émettre l'appel de fonds"
      message="Cette action va comptabiliser l'appel de fonds (crédit 701/102/105, débit 450xxx). Voulez-vous continuer ?"
      confirmLabel="Émettre"
      colorPalette="purple"
      icon={<FileCheck size={24} />}
      isLoading={issueFundCall.isPending}
    />

    {/* Dialog Envoyer */}
    <ConfirmDialog
      isOpen={sendDialogOpen}
      onClose={() => setSendDialogOpen(false)}
      onConfirm={handleSend}
      title="Envoyer l'appel de fonds"
      message="Voulez-vous envoyer cet appel de fonds aux copropriétaires ?"
      confirmLabel="Envoyer"
      colorPalette="blue"
      icon={<Send size={24} />}
      isLoading={sendFundCall.isPending}
    />

    {/* Dialog Supprimer */}
    {fundCall && (
      <ConfirmDeleteDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        entityName={fundCall.label}
        entityType="appel de fonds"
      />
    )}
  </>
  )
}
